import { Box, Button, Stack, Typography, useTheme } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '../../../store'
import { AppActions } from '../../../store/app'
import {
  UIStatus,
  WaterQualityIndexSelectors,
} from '../../../store/waterQualityIndex'

interface StoryScreen5Props {
  onContinue: () => void
}

const getLeadCopy = (status: UIStatus): { heading: string; sub: string } => {
  switch (status) {
    case 'unsafe':
      return {
        heading: 'You have your answer. Now choose how you act on it.',
        sub: "Don't let this be a one-time scare. The water doesn't stop changing just because you stopped looking.",
      }
    case 'cautious':
      return {
        heading: 'You have your answer. Now choose how you act on it.',
        sub: 'A few things are worth watching. Pick how closely you want to keep an eye on them.',
      }
    case 'safe':
    default:
      return {
        heading: 'You have your answer. Now choose how you stay ahead of it.',
        sub: "Things look good today. The only question is whether you'd like to know if that ever changes.",
      }
  }
}

const PRO_BULLETS = [
  'Ongoing testing throughout the year',
  'Safety alerts when water quality changes in your area',
  "A year's filter supply for the equipment you already have",
  'Simple, proactive protection — no guesswork',
]

export const StoryScreen5 = ({ onContinue }: StoryScreen5Props) => {
  const theme = useTheme()
  const dispatch = useDispatch<AppDispatch>()
  const status = useSelector(WaterQualityIndexSelectors.selectWaterSafetyStatus)
  const { heading, sub } = getLeadCopy(status)

  const handleShare = () => {
    dispatch(AppActions.toggleShareResultsModal())
  }

  const darkTeal = theme.palette.primary.dark
  const borderColor = theme.palette.grey[200]

  return (
    <Stack spacing={2.5} sx={{ width: '100%', py: 2 }}>
      {/* Lead */}
      <Stack spacing={1}>
        <Typography sx={{ fontWeight: 700, fontSize: 20, lineHeight: 1.3 }}>
          {heading}
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.5 }}>
          {sub}
        </Typography>
      </Stack>

      {/* Path A — Free report */}
      <Stack
        spacing={0.75}
        sx={{
          border: `1px solid ${borderColor}`,
          borderRadius: '10px',
          p: 2,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
          Keep your free report
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.5 }}>
          You've got your snapshot. Take it with you, share it, make one-time
          changes if you like — then go back to hoping nothing shifts.
        </Typography>
      </Stack>

      {/* Path B — SIPP Pro */}
      <Stack
        spacing={1.25}
        sx={{
          border: `2px solid ${darkTeal}`,
          borderRadius: '10px',
          p: 2,
          backgroundColor: theme.palette.grey[50],
        }}
      >
        <Box
          sx={{
            alignSelf: 'flex-start',
            backgroundColor: darkTeal,
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            borderRadius: '4px',
            px: 1,
            py: 0.5,
          }}
        >
          Recommended
        </Box>

        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
          Go proactive with SIPP Pro
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.5 }}>
          For less than you already spend on filters, swap guesswork for simple
          insight — so you never have to wonder again.
        </Typography>

        <Stack spacing={0.75} sx={{ mt: 0.5 }}>
          {PRO_BULLETS.map((item) => (
            <Stack
              key={item}
              direction="row"
              alignItems="flex-start"
              spacing={1}
            >
              <Typography sx={{ fontSize: 13, color: darkTeal, fontWeight: 700, lineHeight: 1.5 }}>
                ✓
              </Typography>
              <Typography sx={{ fontSize: 13, lineHeight: 1.5 }}>
                {item}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>

      {/* Primary CTA + secondary Share */}
      <Stack spacing={1}>
        <Button
          fullWidth
          variant="contained"
          onClick={onContinue}
          sx={{
            backgroundColor: darkTeal,
            color: '#fff',
            borderRadius: '8px',
            py: '12px',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: darkTeal,
              opacity: 0.9,
            },
          }}
        >
          See your personalized recommendations →
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={handleShare}
          sx={{
            borderColor: darkTeal,
            color: darkTeal,
            borderRadius: '8px',
            py: '10px',
            fontWeight: 600,
            textTransform: 'none',
          }}
        >
          Share your results 🔗
        </Button>
      </Stack>
    </Stack>
  )
}
